import React, { Fragment } from 'react'
import { useCallback, useMemo, useEffect } from 'sb-hooks'
import { useConnect } from 'sb-redux-hook'
import { Modal, modalVisibility, openModal } from 'sb-modal'
import type { ModalVisibilityProps, ModalProps } from 'sb-modal'
import actions from 'redux/actions'
import { constants, links, snowplow } from 'common/helpers'
import type { ResubscribeType } from 'common/helpers/constants/resubscribeTypes'

import { useFreeProduct, useNextProduct } from 'compositions/resubscribe/util'

import { Box, Card } from 'components/_new/layout'
import DeliveryProductListCard from 'components/_new/product/DeliveryProductListCard/DeliveryProductListCard'
import DeliveryProductListCardSkeleton from 'components/_new/product/DeliveryProductListCardSkeleton/DeliveryProductListCardSkeleton'
import Text from 'components/_new/typography/Text/Text'

import type { AddToQueueMetadata, AddToProduct } from 'components/_new/controls'

import messages from './messages'


type ResubscribeConfirmationModalProps = {
  resubscribeCode?: ResubscribeType
  placement: string
} & AllOrNothing<{
  productToAdd?: AddToProduct
  metadata?: AddToQueueMetadata
}>

const ResubscribeConfirmationModal: React.FunctionComponent<ModalVisibilityProps & ResubscribeConfirmationModalProps> = (props) => {
  const { resubscribeCode, productToAdd, metadata, placement, closeModal, onClose } = props

  const { gender } = useConnect({
    gender: 'models.me.gender',
  })

  const { data: nextProductData, month, isFetching: isFetchingNextProduct } = useNextProduct(productToAdd)
  const { data: freeProductData, isFetching: isFetchingFreeProduct } = useFreeProduct(resubscribeCode)

  const handleConfirm = useCallback(() => {
    const mainPageLink = gender === 'male' ? links.subscription.mainMen : links.subscription.mainWomen

    closeModal()

    actions.models.subscription.resubscribe({
      popup: placement,
      resubscribeCode,
      withErrorHandler: false,
      withSuccessModal: false,
      withErrorRedirect: false,
    })
      .then(() => {
        actions.models.router.push(`${mainPageLink}?resubscribe-success`)

        if (productToAdd) {
          actions.models.queue.addItem(productToAdd, metadata, 'FIRST')
        }
      })
      .catch(({ isRedirected }) => {
        if (!isRedirected) {
          if (productToAdd) {
            actions.models.queue.addItemPostponed(productToAdd, metadata, 'FIRST')
          }

          const redirectLink = resubscribeCode === constants.resubscribeTypes.resubfreeproduct
            ? links.cardUpdateFreePerfume // TODO duct-tape, should be removed with ab-test "site:cardUpdate" - added on 07.07.2020 by sonatskiy
            : links.cardUpdate

          actions.misc.cookies.setSessionItem(constants.cookieNames.resubscribeCode, resubscribeCode)
          actions.models.router.push(redirectLink)
        }
      })
  }, [ closeModal, productToAdd, metadata, placement, gender, resubscribeCode ])

  const deliveryDate = useMemo(() => ({ month }), [ month ])

  const { title, text } = useMemo(() => {
    const textsByCode = messages[resubscribeCode] || {}

    return ({
      title: productToAdd ? messages.queueTitle : messages.title,
      text: productToAdd
        ? textsByCode.queueText || messages.queueText
        : textsByCode.text || messages.text,
    })
  }, [ productToAdd, resubscribeCode ])

  const buttonProps: ModalProps['primaryButton'] = useMemo(() => ({
    title: resubscribeCode ? messages.confirm : messages.resubscribeNow,
    rightIcon: resubscribeCode ? null : 'rightArrowSmall',
    onClick: handleConfirm,
  }), [ handleConfirm, resubscribeCode ])

  useEffect(() => {
    snowplow.track('Resubscribe confirmation popup')
  }, [])

  return (
    <Modal
      className="humbleBgColor"
      title={title}
      text={text}
      primaryButton={buttonProps}
      dataTestId="resubConfirmModal"
      onClose={onClose}
    >
      <Box mt={32}>
        {
          Boolean(nextProductData || isFetchingNextProduct) && (
            <Fragment>
              {
                Boolean(nextProductData && !resubscribeCode) && (
                  <Box mb={8} noWrapper>
                    <Text message={messages.queueLabel} size="12-16" semibold uppercase letterSpacing />
                  </Box>
                )
              }
              <Card mb={8} p={16} shadow="warm-10">
                {
                  nextProductData ? (
                    <DeliveryProductListCard
                      name={nextProductData.name}
                      brand={nextProductData.brand}
                      image={nextProductData.image}
                      deliveryDate={deliveryDate}
                    />
                  ) : (
                    <DeliveryProductListCardSkeleton />
                  )
                }
              </Card>
            </Fragment>
          )
        }
        {
          Boolean(freeProductData || isFetchingFreeProduct) && (
            <Card p={16} shadow="warm-10">
              {
                freeProductData ? (
                  <DeliveryProductListCard
                    name={freeProductData.name}
                    brand={freeProductData.brand}
                    image={freeProductData.image}
                    label={messages.gift}
                    deliveryDate={deliveryDate}
                  />
                ) : (
                  <DeliveryProductListCardSkeleton />
                )
              }
            </Card>
          )
        }
      </Box>
    </Modal>
  )
}


export const openResubscribeConfirmationModal = (props?: ResubscribeConfirmationModalProps) => {
  openModal('resubscribeConfirmation', props)
}

export default modalVisibility('resubscribeConfirmation', ResubscribeConfirmationModal)
